import { batchCollectionsCount } from '@wix/ambassador-data-autocms-v1-batch-response/http'
import type {
  BatchCollectionsCountRequest,
  Segment as SegmentType,
} from '@wix/ambassador-data-autocms-v1-batch-response/types'
import { Segment } from '@wix/ambassador-data-autocms-v1-batch-response/types'
import type { HttpClient } from '@wix/http-client'
import type { CollectionsApi } from '@wix/wix-code-collections-api'
import type {
  CollectionItemCount,
  DataSettings,
} from '@wix/wix-data-client-common-standalone'
import type { Schema } from '@wix/wix-data-schema-types'
import { difference, mapValues, merge } from 'lodash'
import type { AppApi } from '../appApiTypes'
import {
  isBlocksCollection,
  isFormsCollection,
  isUserCollection,
} from '../business-logic/collections/collectionTypes'

const fetchCollectionsItemCounts = async ({
  httpClient,
  gridAppId,
  collectionList,
  segment,
}: {
  httpClient: HttpClient
  gridAppId: string | undefined
  collectionList: Schema[]
  segment: SegmentType
}): Promise<Record<string, number>> => {
  const payload: BatchCollectionsCountRequest = {
    items: collectionList.map(schema => ({
      segment,
      appId: gridAppId,
      dataCollectionId: schema.id,
      options: {
        pluginOptions: {
          showDrafts: true,
        },
      },
    })),
  }
  const {
    data: { items },
  } = await httpClient.request(batchCollectionsCount(payload))

  return (items || []).reduce(
    (acc, { dataCollectionId, totalCount }) =>
      dataCollectionId === undefined || totalCount === undefined
        ? acc
        : { ...acc, [dataCollectionId]: totalCount },
    {} as Record<string, number>,
  )
}

interface ItemCounts {
  liveCount?: number
  sandboxCount?: number
}

type ItemCountsByCollection = Record<string, ItemCounts>

const getCollectionsItemCounts = async ({
  httpClient,
  getGridAppId,
  collections,
  getDataSettings,
}: {
  httpClient: HttpClient
  getGridAppId: () => Promise<string | undefined>
  collections: Schema[]
  getDataSettings: () => Promise<DataSettings>
}): Promise<ItemCountsByCollection> => {
  const filteredCollections = collections.filter(
    collection =>
      isBlocksCollection(collection) ||
      isUserCollection(collection) ||
      isFormsCollection(collection),
  )

  if (filteredCollections.length === 0) {
    return {}
  }

  const [gridAppId, { sandboxEnabled }] = await Promise.all([
    getGridAppId(),
    getDataSettings(),
  ])

  const fetchCounts = (segment: SegmentType) =>
    fetchCollectionsItemCounts({
      httpClient,
      gridAppId,
      collectionList: filteredCollections,
      segment,
    })

  if (sandboxEnabled) {
    const [liveItems, sandboxItems] = await Promise.all([
      fetchCounts(Segment.LIVE),
      fetchCounts(Segment.SANDBOX),
    ])

    return merge(
      {},
      mapValues(liveItems, count => ({ liveCount: count })),
      mapValues(sandboxItems, count => ({ sandboxCount: count })),
    )
  }

  const liveItems = await fetchCounts(Segment.SANDBOX)
  return mapValues(liveItems, count => ({ liveCount: count }))
}

type State = {
  counts: {
    [collectionId: string]: ItemCounts & {
      promise?: Promise<ItemCountsByCollection>
    }
  }
  allCountsPromise?: Promise<ItemCountsByCollection>
}

export const createCollectionItemCountsManager = ({
  finalAppApiPromise,
  httpClient,
  collectionsApi,
  getDataSettings,
  getGridAppId,
}: {
  finalAppApiPromise: Promise<AppApi>
  httpClient: HttpClient
  collectionsApi: CollectionsApi
  getDataSettings: () => Promise<DataSettings>
  getGridAppId: () => Promise<string | undefined>
}) => {
  const fetchCounts = async (collections: Schema[]) =>
    getCollectionsItemCounts({
      httpClient,
      getGridAppId,
      collections,
      getDataSettings,
    })

  const state: State = { counts: {} }

  const waitForRequests = async () => {
    const ids = Object.keys(state.counts)

    const requests = ids
      .map(id => state.counts[id]?.promise)
      .concat(state.allCountsPromise)
      .filter(Boolean)

    await Promise.all(requests)
  }

  const removeObsoleteIdsFromState = (
    previousIds: string[],
    newIds: string[],
  ) => {
    const idsToRemove = difference(previousIds, newIds)

    idsToRemove.forEach(id => {
      delete state.counts[id]
    })
  }

  const cancelItemLevelRequests = () => {
    Object.keys(state.counts).forEach(id => {
      delete state.counts[id].promise
    })
  }

  const updateStateEntry = (id: string, payload: ItemCountsByCollection) => {
    if (payload[id]) {
      state.counts[id] = payload[id]
    } else {
      delete state.counts[id]
    }
  }

  const mapCounts = (
    counts: State['counts'],
    type: 'liveCount' | 'sandboxCount',
  ): CollectionItemCount[] =>
    Object.entries(counts)
      .filter(([_, entry]) => entry[type] !== undefined)
      .map(([collectionId, entry]) => ({
        collectionId,
        itemCount: entry[type]!,
      }))

  const getAllCounts = async () => {
    await waitForRequests()
    const appApi = await finalAppApiPromise
    const { sandboxEnabled } = await appApi.getDataSettings()

    return {
      liveItems: mapCounts(state.counts, 'liveCount'),
      sandboxItems: sandboxEnabled
        ? mapCounts(state.counts, 'sandboxCount')
        : undefined,
    }
  }

  const updateAppState = async () => {
    const appApi = await finalAppApiPromise
    const collectionItemCounts = await getAllCounts()

    appApi.updateAppState({ collectionItemCounts })
  }

  const fetchAllCounts = async () => {
    const request = async () => {
      const collections = await collectionsApi.getAll()
      return fetchCounts(collections)
    }

    const allCountsPromise = request()

    state.allCountsPromise = allCountsPromise
    cancelItemLevelRequests()

    try {
      await allCountsPromise

      if (state.allCountsPromise === allCountsPromise) {
        const payload = await allCountsPromise
        const collectionIds = Object.keys(payload)

        collectionIds.forEach(id => {
          if (!state.counts[id]?.promise) {
            updateStateEntry(id, payload)
          }
        })

        const stateIds = Object.keys(state.counts)
        removeObsoleteIdsFromState(stateIds, collectionIds)

        await updateAppState()
      }
    } finally {
      if (state.allCountsPromise === allCountsPromise) {
        delete state.allCountsPromise
      }
    }
  }

  const fetchGivenCounts = async (collectionIds: string[]) => {
    const request = async () => {
      const collections = await collectionsApi.getSome(collectionIds)
      return fetchCounts(collections)
    }

    const promise = request()

    await Promise.all(
      collectionIds.map(async id => {
        state.counts[id] = { ...state.counts[id], promise }

        try {
          await promise

          if (state.counts[id]?.promise === promise) {
            const payload = await promise
            updateStateEntry(id, payload)
          }
        } finally {
          if (state.counts[id]?.promise === promise) {
            delete state.counts[id].promise
          }
        }
      }),
    )

    await updateAppState()
  }

  return {
    getCounts: getAllCounts,
    refreshCounts: async (collectionIds?: string[]) => {
      if (collectionIds) {
        await fetchGivenCounts(collectionIds)
      } else {
        await fetchAllCounts()
      }
    },
  }
}

export type CollectionItemCountsManager = ReturnType<
  typeof createCollectionItemCountsManager
>
